import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { TipoUsuario } from './core/enum/TipoUsuario';
import { AuthGuard } from './core/guards/auth';
import { PermissaoGuard } from './core/guards/permissao';
import { RoleGuard } from './core/guards/role';
import { TipoPermissaoUsuario } from './core/models/usuario/TipoPermissaoUsuario';
import { ROTAS_DO_ASSISTENTE_IZII } from './features/assistente-de-faturas/assistente-izii.routes';

const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'dashboard',
  },
  {
    path: 'estrutura-organizacional',
    loadChildren: () =>
      import(
        './features/estrutura-organizacional/estrutura-organizacional.module'
      ).then((m) => m.EstruturaOrganizacionalModule),
    canActivate: [AuthGuard, PermissaoGuard],
    data: {
      permissao: TipoPermissaoUsuario.EstruturaDaEmpresa,
    },
  },
  {
    // Credencial de portal de operadora: só IziiAdmin. Esta tela grava o que
    // dá acesso aos portais das operadoras — não é tela de usuário comum.
    // O Assistente Izii repete esta regra para esconder o botão desta tela
    // (EXIGENCIAS_DAS_ROTAS em botoes-do-assistente.ts): mudou aqui, muda lá.
    path: 'credenciais-rpa',
    loadChildren: () =>
      import('./features/credenciais-rpa/credenciais-rpa.module').then(
        (m) => m.CredenciaisRpaModule
      ),
    canActivate: [RoleGuard, AuthGuard],
    data: {
      roles: [TipoUsuario.IziiAdmin],
    },
  },
  {
    // O Assistente Izii repete esta regra para esconder os botões de contrato
    // (EXIGENCIAS_DAS_ROTAS em botoes-do-assistente.ts): mudou aqui, muda lá.
    path: 'contratos',
    loadChildren: () =>
      import('./features/contrato/contrato.module').then(
        (m) => m.ContratoModule
      ),
    canActivate: [AuthGuard, PermissaoGuard],
    data: {
      permissao: TipoPermissaoUsuario.Contratos,
    },
  },
  {
    path: 'provedores',
    loadChildren: () =>
      import('./features/provedor/provedor.module').then(
        (m) => m.ProvedorModule
      ),
    canActivate: [AuthGuard, PermissaoGuard],
    data: {
      permissao: TipoPermissaoUsuario.Provedores,
    },
  },
  {
    path: 'beneficiarios',
    loadChildren: () =>
      import('./features/beneficiario/beneficiario.module').then(
        (m) => m.BeneficiarioModule
      ),
    canActivate: [AuthGuard, PermissaoGuard],
    data: {
      permissao: TipoPermissaoUsuario.Beneficiarios,
    },
  },
  {
    path: 'integracoes',
    loadChildren: () =>
      import('./features/integracao/integracao.module').then(
        (m) => m.IntegracaoModule
      ),
    canActivate: [AuthGuard, PermissaoGuard],
    data: {
      permissao: TipoPermissaoUsuario.Integracoes,
    },
  },
  {
    path: 'mov-cad',
    loadChildren: () =>
      import(
        './features/movimentacao-cadastral/movimentacao-cadastral.module'
      ).then((m) => m.MovimentacaoCadastralModule),
    canActivate: [AuthGuard, PermissaoGuard],
    data: {
      permissao: TipoPermissaoUsuario.Importacoes,
    },
  },
  {
    path: 'usuario',
    loadChildren: () =>
      import('./features/usuario/usuario.module').then((m) => m.UsuarioModule),
    canActivate: [AuthGuard],
  },
  {
    path: 'area-administrativa',
    loadChildren: () =>
      import('./features/area-administrativa/area-administrativa.module').then(
        (m) => m.AreaAdministrativaModule
      ),
    canActivate: [RoleGuard, AuthGuard],
    data: {
      roles: [TipoUsuario.IziiAdmin, TipoUsuario.Admin],
    },
  },
  {
    path: 'mov-cad-manual',
    loadChildren: () =>
      import(
        './features/movimentacao-cadastral-manual/movimentacao-cadastral-manual.module'
      ).then((m) => m.MovimentacaoCadastralManualModule),
    canActivate: [AuthGuard],
  },

  {
    path: 'painel-de-atendimento',
    loadChildren: () =>
      import(
        './features/painel-de-atendimento/painel-de-atendimento.module'
      ).then((m) => m.PainelDeAtendimentoModule),
    canActivate: [AuthGuard],
  },
  {
    path: 'painel-de-tarefas',
    loadChildren: () =>
      import('./features/painel-de-tarefas/painel-de-tarefas.module').then(
        (m) => m.PainelDeTarefasModule
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'politica-privacidade',
    loadChildren: () =>
      import(
        './features/politica-privacidade/politica-privacidade.module'
      ).then((m) => m.PolicitaPrivacidadeModule),
  },
  {
    path: 'faturamento',
    loadChildren: () =>
      import('./features/faturamento/faturamento.module').then(
        (m) => m.FaturamentoModule
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'gestao-dos-portais',
    loadChildren: () =>
      import('./features/gestao-dos-portais/gestao-dos-portais.module').then(
        (m) => m.GestaoDosPortaisModule
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'conferencia-de-fatura',
    loadChildren: () =>
      import('./features/batimento-de-fatura/batimento-de-fatura.module').then(
        (m) => m.BatimentoDeFaturaModule
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'dashboard-faturas',
    loadChildren: () =>
      import('./features/dashboard-faturas/dashboard-faturas.module').then(
        (m) => m.DashboardFaturasModule
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'extracao-criticas',
    loadChildren: () =>
      import('./features/extracao-criticas/extracao-criticas.module').then(
        (m) => m.ExtracaoCriticasModule
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'pedido-de-carga',
    loadChildren: () =>
      import('./features/pedido-de-carga/pedido-de-carga.module').then(
        (m) => m.PedidoDeCargaModule
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'corretoras',
    loadChildren: () =>
      import('./features/corretoras/corretoras.module').then(
        (m) => m.CorretorasModule
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'escolhas',
    loadChildren: () =>
      import('./features/escolhas/escolhas.module').then(
        (m) => m.EscolhasModule
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'conciliacao-de-comissao',
    loadChildren: () =>
      import(
        './features/conciliacao-de-comissao/conciliacao-de-comissao.module'
      ).then((m) => m.ConciliacaoDeComissaoModule),
  },
  {
    path: 'comissoes',
    loadChildren: () =>
      import('./features/comissoes/comissoes.module').then(
        (m) => m.ComissoesModule
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'mais-beneficios',
    loadChildren: () =>
      import('./features/mais-beneficios/mais-beneficios.module').then(
        (m) => m.MaisBeneficiosModule
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'rubricas-conciliacao-fatura',
    loadChildren: () =>
      import(
        './features/rubrica-conciliacao-fatura/rubrica-conciliacao-fatura.module'
      ).then((m) => m.RubricaConciliacaoFaturaModule),
    canActivate: [AuthGuard],
  },
  {
    // Leitor de e-mail: mesmo guard das outras telas de Faturas. Quem decide e
    // o backend, que confere o vinculo do usuario com a empresa selecionada e o
    // tipo dele (tipo 2 so IziiAdmin) e devolve 403 com a mensagem (aviso na
    // tela, sem deslogar).
    path: 'remetentes-leitor-email',
    loadChildren: () =>
      import(
        './features/remetentes-leitor-email/remetentes-leitor-email.module'
      ).then((m) => m.RemetentesLeitorEmailModule),
    canActivate: [AuthGuard],
  },
  // Assistente Izii (chat com IA): so dev e local. /assistente-izii e o
  // endereco antigo /assistente-de-faturas, que leva a ele. Com a chave
  // chatIaDisponivel desligada nenhum dos dois casa e a URL cai no '**' abaixo.
  ...ROTAS_DO_ASSISTENTE_IZII,
  {
    path: '**',
    pathMatch: 'full',
    redirectTo: 'dashboard',
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
